<template>
  <div>
    <!-- Navigation Bar -->
    <nav class="navbar navbar-expand-lg navbar-dark bg-primary">
      <div class="container">
        <router-link class="navbar-brand fw-bold" to="/gallery">Design.AppVido</router-link>
        
        <div class="navbar-nav ms-auto d-flex align-items-center">
          <div class="nav-item dropdown">
            <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false" ref="dropdownToggle">
              {{ user.full_name }}
            </a>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><router-link class="dropdown-item" to="/settings">Settings</router-link></li>
              <li v-if="user.is_admin"><router-link class="dropdown-item" to="/admin">Admin Panel</router-link></li>
              <li><hr class="dropdown-divider"></li>
              <li><a class="dropdown-item" href="#" @click="logout">Logout</a></li>
            </ul>
          </div>
        </div>
      </div>
    </nav>

    <!-- Gallery Controls -->
    <div class="container mt-4">
      <div class="row mb-4 gallery-controls-mobile">
        <div class="col-md-6 col-12 mb-3 mb-md-0">
          <div class="btn-group w-100" role="group">
            <button 
              type="button" 
              class="btn btn-outline-primary"
              :class="{ active: viewMode === 'grid' }"
              @click="setViewMode('grid')"
            >
              <i class="fas fa-th"></i> Grid View
            </button>
            <button 
              type="button" 
              class="btn btn-outline-primary"
              :class="{ active: viewMode === 'slideshow' }"
              @click="setViewMode('slideshow')"
            >
              <i class="fas fa-play-circle"></i> Slideshow
            </button>
          </div>
        </div>
        
        <div class="col-md-6 col-12">
          <select 
            class="form-select" 
            v-model="selectedCategory"
            @change="loadGallery(true)"
          >
            <option value="">All Categories</option>
            <option v-for="category in categories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="row">
        <div v-for="item in galleryItems" :key="item.id" class="col-lg-3 col-md-4 col-sm-6 mb-4">
          <div class="card gallery-card" @mousedown="captureScrollPosition" @touchstart="captureScrollPosition" @click="openSlideshow(item)">
            <img :src="item.image_url" class="card-img-top gallery-thumbnail" :alt="item.title">
            <div class="card-body d-flex flex-column">
              <h6 class="card-title">{{ item.title }}</h6>
              <p class="card-text text-muted small flex-grow-1 description-truncated">{{ item.description }}</p>
              <span class="badge bg-secondary mt-auto">{{ item.category_name }}</span>
            </div>
          </div>
        </div>
        
        <!-- Loading More Indicator -->
        <div v-if="isLoadingMore" class="col-12 text-center py-3">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading more...</span>
          </div>
        </div>
        
        <div v-if="galleryItems.length === 0" class="col-12 text-center py-5">
          <h4 class="text-muted">No images found</h4>
          <p class="text-muted">{{ selectedCategory ? 'No images in this category' : 'No images uploaded yet' }}</p>
        </div>
      </div>

      <!-- Slideshow View -->
      <div v-else-if="viewMode === 'slideshow' && galleryItems.length > 0" 
           class="slideshow-container-fullscreen"
           @touchstart="handleTouchStart"
           @touchend="handleTouchEnd"
      >
        <!-- Slideshow Controls -->
        <div class="slideshow-controls">
          <div class="slideshow-nav-controls">
            <button class="btn btn-outline-light" @click="prevSlide(true)">
              <i class="fas fa-chevron-left"></i>
            </button>
            
            <button class="btn btn-outline-light mx-2" @click="toggleSlideshow">
              <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
            </button>
            
            <button class="btn btn-outline-light" @click="nextSlide(true)">
              <i class="fas fa-chevron-right"></i>
            </button>
            
            <button class="btn btn-outline-secondary ms-3" @click="setViewMode('grid')">
              <i class="fas fa-th"></i><span v-if="!isMobile"> Back to Grid</span>
            </button>
            
            <button v-if="!isMobile" class="btn btn-outline-warning ms-2" @click="toggleBrowserFullscreen">
              <i :class="isInBrowserFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
              {{ isInBrowserFullscreen ? 'Exit' : 'Browser' }} Fullscreen
            </button>
          </div>
          
          <div class="slideshow-counter">
            <span class="text-light">{{ currentSlideIndex + 1 }} / {{ galleryItems.length }}</span>
          </div>
        </div>
        
        <!-- Main Slideshow Content -->
        <div class="slideshow-content">
          <!-- Photo Information (Left Side) -->
          <div class="slideshow-info-panel">
            <div class="slideshow-info-content">
              <h3 class="text-light mb-3">{{ currentSlide.title }}</h3>
              <!-- Mobile: Truncated description with inline Read more link -->
              <div v-if="isMobile" class="description-container">
                <p class="text-light-secondary mb-3">
                  <span class="description-mobile-truncated">{{ currentSlide.description }}</span>
                  <span 
                    v-if="currentSlide.description && currentSlide.description.length > 100"
                    class="read-more-link ms-1"
                    @click="showDescriptionModal"
                  >
                    Read more
                  </span>
                </p>
              </div>
              <!-- Desktop: Full description -->
              <p v-else class="text-light-secondary mb-3">{{ currentSlide.description }}</p>
              <span v-if="!isMobile" class="badge bg-primary">{{ currentSlide.category_name }}</span>
            </div>
          </div>
          
          <!-- Photo Display (Right Side) -->
          <div class="slideshow-image-panel">
            <div class="slideshow-slide" :class="slideTransitionClass">
              <img 
                :src="currentSlide.image_url" 
                class="slideshow-image-fullheight" 
                :alt="currentSlide.title"
              >
            </div>
          </div>
        </div>
        
        <!-- Thumbnail Navigation Strip -->
        <div v-if="!isMobile || showThumbnailsOnMobile" class="slideshow-thumbnails">
          <div class="thumbnail-strip">
            <button 
              v-for="(item, index) in galleryItems" 
              :key="item.id"
              class="thumbnail-btn"
              :class="{ active: index === currentSlideIndex }"
              @click="goToSlide(index)"
            >
              <img :src="item.image_url" :alt="item.title" class="thumbnail-img">
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen Modal -->
    <div 
      class="modal fade" 
      id="fullscreenModal" 
      tabindex="-1" 
      ref="fullscreenModal"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content bg-dark">
          <div class="modal-header border-0">
            <h5 class="modal-title text-white">{{ currentSlide?.title }}</h5>
            <button v-if="!isMobile" type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body d-flex align-items-center justify-content-center p-0">
            <img 
              :src="currentSlide?.image_url" 
              class="img-fluid" 
              :alt="currentSlide?.title"
              style="max-height: 90vh; max-width: 100%;"
            >
          </div>
          <div class="modal-footer border-0">
            <button class="btn btn-outline-light" @click="prevSlide(true)">
              <i class="fas fa-chevron-left"></i> Previous
            </button>
            <span class="text-white mx-3">{{ currentSlideIndex + 1 }} of {{ galleryItems.length }}</span>
            <button class="btn btn-outline-light" @click="nextSlide(true)">
              Next <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Description Modal -->
    <div 
      class="modal fade" 
      id="descriptionModal" 
      tabindex="-1" 
      ref="descriptionModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ currentSlide?.title }}</h5>
            <button v-if="!isMobile" type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <p>{{ currentSlide?.description }}</p>
            <span class="badge bg-primary">{{ currentSlide?.category_name }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Modal, Dropdown } from 'bootstrap'

export default {
  name: 'GalleryPage',
  data() {
    return {
      user: {},
      galleryItems: [],
      categories: [],
      selectedCategory: '',
      viewMode: 'grid', // 'grid' or 'slideshow'
      currentSlideIndex: 0,
      loading: false,
      slideTransitionClass: '',
      fullscreenModal: null,
      // Pagination
      currentPage: 1,
      hasNextPage: false,
      isLoadingMore: false,
      // Slideshow auto-advance
      slideshowTimer: null,
      isPlaying: false,
      slideshowInterval: 5000, // Default 5 seconds
      // Browser fullscreen
      isInBrowserFullscreen: false,
      // Active slideshow effect
      activeEffect: null,
      // Mobile detection
      isMobile: false,
      isIPadDevice: false,
      // Description modal
      descriptionModal: null,
      // Touch/swipe handling for mobile thumbnails and navigation
      showThumbnailsOnMobile: false,
      touchStartY: 0,
      touchEndY: 0,
      touchStartX: 0,
      touchEndX: 0,
      // Scroll position preservation
      savedScrollPosition: 0
    }
  },
  computed: {
    currentSlide() {
      return this.galleryItems[this.currentSlideIndex] || {}
    }
  },
  methods: {
    async loadGallery(reset = false) {
      if (reset) {
        this.galleryItems = []
        this.currentPage = 1
      }
      
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        
        const params = { 
          page: this.currentPage,
          limit: 50
        }
        if (this.selectedCategory) {
          params.category_id = this.selectedCategory
        }
        
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/gallery', { ...config, params })
        
        // Handle paginated response
        if (response.data.items) {
          if (reset) {
            this.galleryItems = response.data.items
          } else {
            this.galleryItems = [...this.galleryItems, ...response.data.items]
          }
          this.hasNextPage = response.data.pagination.has_next
        } else {
          // Backward compatibility for non-paginated response
          this.galleryItems = response.data
          this.hasNextPage = false
        }
        
        if (reset) {
          this.currentSlideIndex = 0
        }
      } catch (error) {
        console.error('Failed to load gallery:', error)
        if (error.response?.status === 401) {
          this.logout()
        }
      } finally {
        this.loading = false
        this.isLoadingMore = false
      }
    },

    async loadMore() {
      if (this.hasNextPage && !this.isLoadingMore) {
        this.isLoadingMore = true
        this.currentPage++
        await this.loadGallery(false)
      }
    },

    // Handle infinite scroll for grid view
    handleScroll() {
      if (this.viewMode !== 'grid') return
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const windowHeight = window.innerHeight
      const docHeight = document.documentElement.offsetHeight
      
      if (scrollTop + windowHeight >= docHeight - 1000) { // Load more when 1000px from bottom
        this.loadMore()
      }
    },
    
    async loadCategories() {
      try {
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/categories')
        this.categories = response.data
      } catch (error) {
        console.error('Failed to load categories:', error)
      }
    },

    async loadSlideshowSettings() {
      try {
        const token = localStorage.getItem('token')
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        }
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/slideshow-settings', config)
        this.slideshowInterval = response.data.interval * 1000 // Convert to milliseconds
      } catch (error) {
        console.error('Failed to load slideshow settings:', error)
        // Use default interval if settings not available
      }
    },

    async loadActiveEffect() {
      try {
        const response = await axios.get('https://designappvido-backend.up.railway.app/api/slideshow-effects/active')
        this.activeEffect = response.data
      } catch (error) {
        console.error('Failed to load active slideshow effect:', error)
        // Use default effect if none active
        this.activeEffect = { css_class: 'fade-effect' }
      }
    },
    
    setViewMode(mode, preserveIndex = false, skipScrollSave = false) {
      this.stopSlideshow() // Stop any running slideshow
      
      // Save scroll position when switching from grid to slideshow (unless explicitly skipped)
      if (mode === 'slideshow' && this.viewMode === 'grid' && !skipScrollSave) {
        this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop
      }
      
      this.viewMode = mode
      
      // Save view mode preference
      localStorage.setItem('galleryViewMode', mode)
      
      if (mode === 'slideshow' && this.galleryItems.length > 0) {
        if (!preserveIndex) {
          this.currentSlideIndex = 0
        }
        // Auto-enter browser fullscreen for slideshow
        this.enterBrowserFullscreen()
        // Add keyboard event listener for slideshow navigation
        document.addEventListener('keydown', this.handleKeydown)
        // Start auto-advance for both desktop and mobile
        this.startSlideshow()
      } else if (mode === 'grid') {
        this.exitBrowserFullscreen()
        // Remove keyboard event listener when exiting slideshow
        document.removeEventListener('keydown', this.handleKeydown)
        
        // Restore scroll position when returning to grid view
        this.restoreScrollPosition()
      }
    },
    
    prevSlide(isManual = false) {
      if (this.galleryItems.length === 0) return
      
      // Auto-pause slideshow when user manually navigates
      if (isManual && this.isPlaying) {
        this.stopSlideshow()
      }
      
      this.slideTransitionClass = this.activeEffect ? this.activeEffect.css_class : 'fade-effect'
      this.currentSlideIndex = (this.currentSlideIndex - 1 + this.galleryItems.length) % this.galleryItems.length
      setTimeout(() => { this.slideTransitionClass = '' }, 300)
      
      // Check if we need to load more items (reached beginning and has previous page)
      if (this.currentSlideIndex === 0 && this.currentPage > 1) {
        // Could implement loading previous page here if needed
      }
    },
    
    nextSlide(isManual = false) {
      if (this.galleryItems.length === 0) return
      
      // Auto-pause slideshow when user manually navigates
      if (isManual && this.isPlaying) {
        this.stopSlideshow()
      }
      
      this.slideTransitionClass = this.activeEffect ? this.activeEffect.css_class : 'fade-effect'
      this.currentSlideIndex = (this.currentSlideIndex + 1) % this.galleryItems.length
      setTimeout(() => { this.slideTransitionClass = '' }, 300)
      
      // Auto-load next page when reaching the end in slideshow mode
      if (this.viewMode === 'slideshow' && this.currentSlideIndex === this.galleryItems.length - 5 && this.hasNextPage) {
        this.loadMore()
      }
    },

    startSlideshow() {
      if (this.slideshowTimer) {
        clearInterval(this.slideshowTimer)
      }
      this.isPlaying = true
      this.slideshowTimer = setInterval(() => {
        this.nextSlide()
      }, this.slideshowInterval)
    },

    stopSlideshow() {
      if (this.slideshowTimer) {
        clearInterval(this.slideshowTimer)
        this.slideshowTimer = null
      }
      this.isPlaying = false
    },

    toggleSlideshow() {
      if (this.isPlaying) {
        this.stopSlideshow()
      } else {
        this.startSlideshow()
      }
    },

    goToSlide(index) {
      // Auto-pause slideshow when user manually navigates via thumbnails
      if (this.isPlaying) {
        this.stopSlideshow()
      }
      
      this.currentSlideIndex = index
      this.slideTransitionClass = this.activeEffect ? this.activeEffect.css_class : 'fade-effect'
      setTimeout(() => { this.slideTransitionClass = '' }, 300)
    },

    enterBrowserFullscreen() {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen()
        this.isInBrowserFullscreen = true
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen()
        this.isInBrowserFullscreen = true
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen()
        this.isInBrowserFullscreen = true
      }
    },

    exitBrowserFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
        this.isInBrowserFullscreen = false
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
        this.isInBrowserFullscreen = false
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen()
        this.isInBrowserFullscreen = false
      }
    },

    toggleBrowserFullscreen() {
      if (this.isInBrowserFullscreen) {
        this.exitBrowserFullscreen()
      } else {
        this.enterBrowserFullscreen()
      }
    },
    
    captureScrollPosition() {
      // Capture scroll position on mousedown, before any browser scroll behavior
      if (this.viewMode === 'grid') {
        this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop
      }
    },

    restoreScrollPosition() {
      // Enhanced scroll restoration with iPad-specific handling
      const scrollTo = (targetPosition) => {
        if (this.isIPad()) {
          // iPad requires more robust scroll restoration
          // Use multiple approaches to ensure it works across different iPad scenarios
          
          // Method 1: Immediate scroll
          window.scrollTo(0, targetPosition)
          document.documentElement.scrollTop = targetPosition
          document.body.scrollTop = targetPosition
          
          // Method 2: requestAnimationFrame for smoother restoration
          requestAnimationFrame(() => {
            window.scrollTo(0, targetPosition)
            document.documentElement.scrollTop = targetPosition
            
            // Method 3: Additional delay for iPad's rendering cycle
            setTimeout(() => {
              window.scrollTo({
                top: targetPosition,
                left: 0,
                behavior: 'instant'
              })
            }, 50)
          })
        } else {
          // Standard scroll restoration for other devices
          window.scrollTo(0, targetPosition)
        }
      }

      // Use $nextTick to ensure DOM is updated first
      this.$nextTick(() => {
        scrollTo(this.savedScrollPosition)
      })
    },
    
    openSlideshow(item) {
      const index = this.galleryItems.findIndex(i => i.id === item.id)
      if (index !== -1) {
        this.currentSlideIndex = index
        // Skip scroll save since we already captured it in captureScrollPosition
        this.setViewMode('slideshow', true, true) // true to preserve index, true to skip scroll save
      }
    },
    
    toggleFullscreen() {
      if (!this.fullscreenModal) {
        this.fullscreenModal = new Modal(this.$refs.fullscreenModal)
      }
      this.fullscreenModal.show()
    },
    
    async logout() {
      try {
        const token = localStorage.getItem('token')
        if (token) {
          // Call backend logout endpoint
          await axios.post('https://designappvido-backend.up.railway.app/api/logout', {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      } catch (error) {
        console.error('Logout error:', error)
        // Continue with local logout even if backend call fails
      } finally {
        // Always clear local storage and redirect
        localStorage.removeItem('token')
        this.$router.push('/')
      }
    },

    handleFullscreenChange() {
      this.isInBrowserFullscreen = !!(document.fullscreenElement || 
                                      document.webkitFullscreenElement || 
                                      document.msFullscreenElement)
      
      // If exiting fullscreen while in slideshow mode, stop the slideshow
      if (!this.isInBrowserFullscreen && this.viewMode === 'slideshow') {
        this.stopSlideshow()
        this.setViewMode('grid')
      }
    },

    // Mobile detection utility
    detectMobile() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      this.isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase()) || 
                     window.innerWidth <= 768
    },

    // iPad detection utility
    isIPad() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      return /ipad/i.test(userAgent.toLowerCase()) || 
             (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    },

    // Show description in modal popup
    showDescriptionModal() {
      if (!this.descriptionModal) {
        this.descriptionModal = new Modal(this.$refs.descriptionModal)
      }
      this.descriptionModal.show()
    },

    // Touch event handlers for swipe detection
    handleTouchStart(event) {
      this.touchStartY = event.touches[0].clientY
      this.touchStartX = event.touches[0].clientX
    },

    handleTouchEnd(event) {
      this.touchEndY = event.changedTouches[0].clientY
      this.touchEndX = event.changedTouches[0].clientX
      this.handleSwipe()
    },

    handleSwipe() {
      const swipeDistanceY = this.touchStartY - this.touchEndY
      const swipeDistanceX = this.touchStartX - this.touchEndX
      const minSwipeDistance = 50 // Minimum distance for a swipe

      // Check if this is primarily a horizontal or vertical swipe
      const isHorizontalSwipe = Math.abs(swipeDistanceX) > Math.abs(swipeDistanceY)
      
      if (isHorizontalSwipe && this.viewMode === 'slideshow') {
        // Horizontal swipes for navigation
        if (swipeDistanceX > minSwipeDistance) {
          // Swipe left to go to next slide
          this.nextSlide(true) // true indicates manual navigation
        }
        else if (swipeDistanceX < -minSwipeDistance) {
          // Swipe right to go to previous slide
          this.prevSlide(true) // true indicates manual navigation
        }
      } else if (!this.isMobile) {
        // Vertical swipes for thumbnail visibility (only on desktop)
        if (swipeDistanceY > minSwipeDistance) {
          // Swipe up to show thumbnails
          this.showThumbnailsOnMobile = true
        }
        else if (swipeDistanceY < -minSwipeDistance) {
          // Swipe down to hide thumbnails
          this.showThumbnailsOnMobile = false
        }
      }
    },

    // Keyboard event handler for slideshow navigation
    handleKeydown(event) {
      // Only handle keyboard navigation when in slideshow mode
      if (this.viewMode !== 'slideshow') return

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault() // Prevent page scrolling
          this.prevSlide(true) // true indicates manual navigation
          break
        case 'ArrowRight':
          event.preventDefault() // Prevent page scrolling
          this.nextSlide(true) // true indicates manual navigation
          break
      }
    }
  },
  
  async mounted() {
    // Initialize mobile detection
    this.detectMobile()
    this.isIPadDevice = this.isIPad()
    
    // Load saved view mode preference
    const savedViewMode = localStorage.getItem('galleryViewMode')
    if (savedViewMode && ['grid', 'slideshow'].includes(savedViewMode)) {
      this.viewMode = savedViewMode
    }

    // Token validation is now handled by router guard
    // Just fetch user info since we know the token is valid
    try {
      const token = localStorage.getItem('token')
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      }
      const response = await axios.get('https://designappvido-backend.up.railway.app/api/me', config)
      this.user = response.data
    } catch (error) {
      if (error.response?.status === 401) {
        // Fallback: if session becomes invalid after router guard check
        localStorage.removeItem('token')
        this.$router.push('/')
        return
      }
    }
    
    // Load data
    await this.loadCategories()
    await this.loadSlideshowSettings()
    await this.loadActiveEffect()
    await this.loadGallery(true)
    
    // Set up infinite scroll for grid view
    window.addEventListener('scroll', this.handleScroll)
    
    // Handle fullscreen change events
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.addEventListener('msfullscreenchange', this.handleFullscreenChange)
    
    // If slideshow mode was saved, apply it (with mobile-aware autoplay)
    if (this.viewMode === 'slideshow' && this.galleryItems.length > 0) {
      this.$nextTick(() => {
        this.enterBrowserFullscreen()
        // Add keyboard event listener for slideshow navigation
        document.addEventListener('keydown', this.handleKeydown)
        // Only start autoplay on desktop, not mobile
        if (!this.isMobile) {
          this.startSlideshow()
        }
      })
    }
    
    // Initialize Bootstrap dropdown manually
    this.$nextTick(() => {
      if (this.$refs.dropdownToggle) {
        new Dropdown(this.$refs.dropdownToggle)
      }
    })
    
    // Update mobile detection on window resize
    window.addEventListener('resize', () => {
      this.detectMobile()
      this.isIPadDevice = this.isIPad()
    })
  },

  beforeUnmount() {
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', () => {
      this.detectMobile()
      this.isIPadDevice = this.isIPad()
    })
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('msfullscreenchange', this.handleFullscreenChange)
    // Clean up keyboard event listener
    document.removeEventListener('keydown', this.handleKeydown)
    
    // Stop slideshow timer
    this.stopSlideshow()
  }
}
</script>

<style scoped>
.bg-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.gallery-card {
  cursor: pointer;
  transition: transform 0.2s;
  height: 100%;
}

.gallery-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.gallery-thumbnail {
  height: 200px;
  object-fit: cover;
}

/* Mobile: Show full height photos instead of cropping */
@media (max-width: 768px) {
  .gallery-thumbnail {
    height: auto;
    max-height: none;
    object-fit: contain;
    width: 100%;
  }
}

/* Description truncation for mobile slideshow */
.description-mobile-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 3em;
}

/* Read more link styling */
.read-more-link {
  color: #007bff !important;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.8rem;
}

.read-more-link:hover {
  color: #0056b3 !important;
  text-decoration: none;
}

/* Description truncation for equal card heights */
.description-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 4.5em; /* Approximate height for 3 lines */
}

/* Slideshow Fullscreen Layout */
.slideshow-container-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 1050;
  display: flex;
  flex-direction: column;
}

.slideshow-controls {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  z-index: 1060;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slideshow-nav-controls {
  display: flex;
  align-items: center;
}

.slideshow-counter {
  font-size: 1.1rem;
  font-weight: 500;
}

.slideshow-content {
  flex: 1;
  display: flex;
  min-height: 0;
  margin-top: 80px;
  margin-bottom: 100px;
}

.slideshow-info-panel {
  width: 300px;
  padding: 20px;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.slideshow-info-content {
  width: 100%;
}

.slideshow-image-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.slideshow-image-fullheight {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.slideshow-slide {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease-in-out;
}

/* Slideshow effect classes */
.fade-effect {
  animation: fadeTransition 0.5s ease-in-out;
}

.slide-left-effect {
  animation: slideLeftTransition 0.5s ease-in-out;
}

.slide-right-effect {
  animation: slideRightTransition 0.5s ease-in-out;
}

.zoom-in-effect {
  animation: zoomInTransition 0.5s ease-in-out;
}

.flip-effect {
  animation: flipTransition 0.5s ease-in-out;
}

@keyframes fadeTransition {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideLeftTransition {
  from { 
    opacity: 0;
    transform: translateX(-100%);
  }
  to { 
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideRightTransition {
  from { 
    opacity: 0;
    transform: translateX(100%);
  }
  to { 
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes zoomInTransition {
  from { 
    opacity: 0;
    transform: scale(0.5);
  }
  to { 
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes flipTransition {
  from { 
    opacity: 0;
    transform: rotateY(-90deg);
  }
  to { 
    opacity: 1;
    transform: rotateY(0);
  }
}

.slide-left, .slide-right, .slide-fade {
  animation: slideTransition 0.3s ease-in-out;
}

@keyframes slideTransition {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Thumbnail Navigation Strip */
.slideshow-thumbnails {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1060;
  max-width: 80%;
}

.thumbnail-strip {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 10px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

.thumbnail-strip::-webkit-scrollbar {
  height: 6px;
}

.thumbnail-strip::-webkit-scrollbar-track {
  background: transparent;
}

.thumbnail-strip::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.thumbnail-btn {
  flex-shrink: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 2px;
  background: transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}

.thumbnail-btn.active {
  border-color: #fff;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.thumbnail-btn:hover {
  border-color: rgba(255, 255, 255, 0.7);
}

.thumbnail-img {
  width: 60px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  display: block;
}

.text-light-secondary {
  color: rgba(255, 255, 255, 0.8) !important;
}

.btn-outline-primary.active {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: white;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  /* Full width gallery controls on mobile */
  .gallery-controls-mobile .btn-group {
    width: 100% !important;
  }
  
  .gallery-controls-mobile .btn-group .btn {
    flex: 1;
  }
  
  .slideshow-content {
    flex-direction: column;
  }
  
  .slideshow-info-panel {
    width: 100%;
    height: 120px; /* Reduced from 180px to give more space for photo */
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding: 15px; /* Reduced padding */
  }
  
  /* Smaller fonts for mobile slideshow info */
  .slideshow-info-content h3 {
    font-size: 1rem; /* Smaller title */
    margin-bottom: 0.5rem;
  }
  
  .slideshow-info-content p {
    font-size: 0.8rem; /* Smaller description */
    margin-bottom: 0.5rem;
  }
  
  .slideshow-info-content .badge {
    font-size: 0.7rem; /* Smaller category badge */
  }
  
  /* Mobile slideshow controls in single row */
  .slideshow-controls {
    flex-direction: row;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
    top: 10px;
    left: 10px;
    right: 10px;
  }
  
  .slideshow-nav-controls {
    display: flex;
    justify-content: center;
    gap: 8px;
    flex-wrap: nowrap;
  }
  
  .slideshow-counter {
    text-align: center;
    font-size: 0.8rem; /* Smaller font for mobile */
  }
  
  .thumbnail-strip {
    max-width: 100%;
  }
  
  .thumbnail-img {
    width: 45px;
    height: 30px;
  }
  
  .description-truncated {
    -webkit-line-clamp: 2;
    min-height: 3em;
  }
  
  /* Adjust slideshow content margins for mobile controls */
  .slideshow-content {
    margin-top: 70px; /* Reduced from 110px since controls are now single row */
  }
}

/* Very small screens */
@media (max-width: 576px) {
  .slideshow-info-panel {
    height: 100px; /* Even smaller on very small screens */
    padding: 10px;
  }
  
  .slideshow-info-content h3 {
    font-size: 0.9rem; /* Even smaller title */
  }
  
  .slideshow-info-content p {
    font-size: 0.75rem; /* Even smaller description */
  }
  
  .slideshow-controls {
    top: 10px;
    left: 10px;
    right: 10px;
  }
  
  .slideshow-thumbnails {
    bottom: 10px;
    max-width: 90%;
  }
}
</style>